import { useDispatch, useSelector } from "react-redux";
import { useEventListener, useReadLocalStorage } from "usehooks-ts";
import { API_USER } from "../../api/user";
import { setBalance, setCurrency, setUserId } from "../../store/userSlice";
import { ENUM_LCS_NAME, ENUM_TOAST_TYPE, TEST_MODE } from "../../store/enums";
import { dispatchCusEvent, messageByCode } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import { errorToast, infoToast, successToast } from "../../components/mui/Toaster";
import { setCaptchaUsing, setProhibitedUnconfirmed } from "../../store/siteSlice";
import { API_CASINO } from "../../api/casino";
import { useRef } from "react";

export const ENUM_COMMON_EVENT = {
  exit: "account.exit",
  favoritesClick: "favorites-click",
  checkOpenModal: "check.open.modal",
  getBalance: "site.balance.get",
  getInfo: "site.info.get",
  getBonus: "site.bonus.get",
  translationToast: "site.translate.toast",
  checkCaptcha: "site.info.captcha",
  sessionError: "site.error.session",
  emailNotConfirmed: "site.error.emailNotConfirmed"
};

export default function useListener() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const start_r = useReadLocalStorage(ENUM_LCS_NAME.startR) || "";
  const isAuth = useSelector((state) => state.user.isAuth);

  const eventSessionError = ENUM_COMMON_EVENT.sessionError;
  const eventExit = ENUM_COMMON_EVENT.exit;
  const eventBalance = ENUM_COMMON_EVENT.getBalance;

  const isProcessErrorMessage = useRef(false);

  const onExit = async () => {
    await API_USER.logout();
    dispatch(setUserId(0));
    dispatch(setBalance({}));
  };

  const getBalance = (DEBUG = false) => {
    if (!isAuth) return;
    if (TEST_MODE) return;

    return API_USER.balance
      .info()
      .then((e) => {
        const data = e.data;

        if (data.errorCode == 1 && data.session === false) {
          console.log("error");
          dispatchCusEvent(ENUM_COMMON_EVENT.exit);
        }

        if (DEBUG) console.log("balance res", e);
        dispatch(setBalance(data));
        for (const currency in data) {
          const info = data[currency]?.info;
          if (info?.current) {
            dispatch(setCurrency(currency));
            break;
          }
        }
      })
      .catch((e) => {
        if (DEBUG) console.log("balance catch", e);
      });
  };

  const checkCaptchaUsing = () => {
    API_CASINO.captchaUsing({ start_r })
      .then((e) => {
        const data = e.data?.data;
        dispatch(setCaptchaUsing(data));
      })
      .catch(console.error);
  };

  useEventListener(eventSessionError, () => {
    dispatchCusEvent(ENUM_COMMON_EVENT.checkOpenModal);
  });

  useEventListener(eventExit, () => {
    onExit();
  });

  useEventListener(eventBalance, () => {
    getBalance();
  });

  const sendCaptchaCheckRef = useRef(false);
  useEventListener(ENUM_COMMON_EVENT.checkCaptcha, () => {
    clearTimeout(sendCaptchaCheckRef.current);
    sendCaptchaCheckRef.current = setTimeout(() => checkCaptchaUsing(), 1000);
  });

  useEventListener(ENUM_COMMON_EVENT.translationToast, (e) => {
    const { message, type } = e.detail;

    if (type === ENUM_TOAST_TYPE.success) {
      successToast(t(message));
    } else if (type === ENUM_TOAST_TYPE.error) {
      if (!isProcessErrorMessage.current) {
        errorToast(t(message));
      }
      isProcessErrorMessage.current = true;
      setTimeout(() => (isProcessErrorMessage.current = false), 300);
    } else if (type === ENUM_TOAST_TYPE.info) {
      infoToast(t(message));
    }
  });

  useEventListener(ENUM_COMMON_EVENT.emailNotConfirmed, () => {
    if (!isAuth) {
      errorToast(t(messageByCode(524)));
      return;
    }

    dispatch(setProhibitedUnconfirmed(true));
  });
}
